import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react"
import type { GameLogicState, SlideGeneric } from "@types"
import { useEffect, useRef, useState } from "react"
import { GameText } from "../../components/GameText"
import defaultGameData from "../default"

export type HistoryEntry = GameLogicState["history"][number]

// History records a card by its English title only, so the story, picture and
// answers are looked up in the compiled content (both decks). Titles are
// unique across the decks; src/features/default.test.ts keeps them so.
export const findSlideByTitle = (title: string): SlideGeneric | null => {
  for (const deck of defaultGameData.content) {
    for (const group of deck.groups) {
      for (const slide of group.slides) {
        if (slide.title.en === title || slide.title.es === title) {
          return slide
        }
      }
    }
  }
  return null
}

/**
 * "best" and "worst": the single-card preview opened from the summary's Best
 * Choice and Most Challenging Choice cards (unchanged). "review": every card
 * of the game just finished, in the order played, one at a time.
 */
export type CardReviewTone = "best" | "worst" | "review"

const TONE = {
  best: { header: "Best Decision", scheme: "green" },
  worst: { header: "Most Challenging Decision", scheme: "red" },
  review: { header: "Your Choices", scheme: "purple" },
} as const

interface CardReviewDialogProps {
  isOpen: boolean
  onClose: () => void
  /** The cards to show, in order; the preview passes one. */
  entries: HistoryEntry[]
  tone: CardReviewTone
}

// Read-only by design: nothing here dispatches to the store. The only buttons
// are Close and, in the review, Previous and Next. Only the current card is
// mounted, so only its picture loads (the slide pictures are precached).
export const CardReviewDialog = ({
  isOpen,
  onClose,
  entries,
  tone,
}: CardReviewDialogProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    size="xl"
    isCentered
    // The review keeps its header and Previous/Next in view and scrolls the
    // card between them; the single-card preview scrolls as before.
    scrollBehavior={tone === "review" ? "inside" : "outside"}
  >
    <ModalOverlay />
    <CardContent entries={entries} tone={tone} />
  </Modal>
)

// Chakra mounts a modal's children only while it is open, so this component
// (and its card index) starts fresh at the first card on every opening.
const CardContent = ({
  entries,
  tone,
}: {
  entries: HistoryEntry[]
  tone: CardReviewTone
}) => {
  const [index, setIndex] = useState(0)
  const bodyRef = useRef<HTMLDivElement>(null)
  const isReview = tone === "review"
  const { header, scheme } = TONE[tone]

  // A new card starts at its top, not where the previous one was scrolled to.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }, [index])

  const entry = entries[Math.min(index, entries.length - 1)]
  const slide = entry?.slide ? findSlideByTitle(entry.slide) : null
  const selectedChoice = entry?.action ?? null
  const last = entries.length - 1

  return (
    <ModalContent>
      <ModalHeader>{header}</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6} ref={bodyRef}>
        {slide ? (
          <VStack spacing={4} align="stretch">
            {/* The whole picture in its own shape, with the story under it;
                19px, as on the game card. A square box with the picture
                cropped to fill it cut off the sides of the (mostly wide)
                slide pictures (client report, 26 October 2026). */}
            <Box
              position="relative"
              width="100%"
              borderRadius="lg"
              overflow="hidden"
            >
              <Image
                key={slide.image}
                src={slide.image}
                srcSet={slide.imageSrcSet}
                sizes="(min-width: 30em) 448px, 100vw"
                decoding="async"
                alt={slide.imageAlt}
                display="block"
                width="100%"
                height="auto"
                // 16:9 holds the space until the picture's shape is known;
                // the two tall pictures shrink rather than fill the screen.
                sx={{ aspectRatio: "auto 16 / 9" }}
                maxH="60vh"
                objectFit="contain"
              />
              <Box
                position="relative"
                width="100%"
                bg="rgba(0, 0, 0, 0.75)"
                color="white"
                p={4}
                fontSize="19px"
                lineHeight="1.45"
              >
                <GameText text={slide.description} />
              </Box>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Scenario
              </Text>
              <Heading size="md">
                <GameText text={slide.title} />
              </Heading>
            </Box>

            {selectedChoice && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Your Choice
                </Text>
                <Badge
                  colorScheme={scheme}
                  fontSize="md"
                  px={3}
                  py={2}
                  whiteSpace="normal"
                  wordBreak="break-word"
                  maxW="100%"
                  display="inline-block"
                >
                  {selectedChoice}
                </Badge>
              </Box>
            )}

            {/* The card's answers, as plain boxes: they cannot be tapped. */}
            {slide.selections.length > 0 && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Available Choices
                </Text>
                <VStack spacing={2} align="stretch">
                  {slide.selections.map((selection, i) => {
                    const isSelected =
                      selection.title.en === selectedChoice ||
                      selection.title.es === selectedChoice
                    return (
                      <Box
                        key={i}
                        p={3}
                        borderRadius="md"
                        borderWidth="2px"
                        borderColor={isSelected ? `${scheme}.500` : "gray.200"}
                        bg={isSelected ? `${scheme}.50` : "gray.50"}
                      >
                        <Flex
                          align="flex-start"
                          gap={2}
                          wrap="wrap"
                          width="100%"
                        >
                          {isSelected && (
                            <Badge colorScheme={scheme} flexShrink={0}>
                              Selected
                            </Badge>
                          )}
                          <Box flex="1" minW={0}>
                            <GameText text={selection.title} />
                          </Box>
                        </Flex>
                      </Box>
                    )
                  })}
                </VStack>
              </Box>
            )}
          </VStack>
        ) : (
          // The card is not in this build's content (renamed after the game
          // was played): show what history recorded.
          <VStack spacing={4} align="stretch">
            {entry?.slide && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Scenario
                </Text>
                <Heading size="md">{entry.slide}</Heading>
              </Box>
            )}
            {selectedChoice && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Your Choice
                </Text>
                <Text fontWeight="bold">{selectedChoice}</Text>
              </Box>
            )}
            <Text>Slide information not available</Text>
          </VStack>
        )}
      </ModalBody>
      {isReview && entries.length > 0 && (
        <ModalFooter justifyContent="space-between" gap={3}>
          <Button
            onClick={() => setIndex(i => Math.max(0, i - 1))}
            isDisabled={index === 0}
          >
            Previous
          </Button>
          <Text fontWeight="semibold" aria-live="polite">
            Card {index + 1} of {entries.length}
          </Text>
          <Button
            colorScheme="purple"
            onClick={() => setIndex(i => Math.min(last, i + 1))}
            isDisabled={index === last}
          >
            Next
          </Button>
        </ModalFooter>
      )}
    </ModalContent>
  )
}
